<!-- Componente WhatsApp Web Conector -->
<template>
  <div class="whatsapp-web-connector">
    <div class="row">
      <!-- Estado de Conexión -->
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">
              <i class="fab fa-whatsapp text-success"></i>
              WhatsApp Web - Conector CentralGym
            </h4>
          </div>
          <div class="card-body">
            <!-- Estado de conexión -->
            <div class="connection-status mb-4">
              <div class="alert" :class="statusClass">
                <div class="d-flex align-items-center">
                  <i :class="statusIcon" class="me-2"></i>
                  <div class="flex-grow-1">
                    <strong>{{ statusTitle }}</strong>
                    <div class="text-sm">{{ statusMessage }}</div>
                  </div>
                  <div class="connection-info" v-if="clientInfo">
                    <small class="text-muted">
                      <i class="fas fa-phone"></i> {{ clientInfo.phone }}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Controles principales -->
            <div class="connection-controls mb-4">
              <div class="row">
                <div class="col-md-8">
                  <button 
                    @click="initializeWhatsApp" 
                    :disabled="loading || isConnecting || isReady"
                    class="btn btn-success me-2"
                  >
                    <i v-if="loading || isConnecting" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fab fa-whatsapp"></i>
                    {{ connectionButtonText }}
                  </button>

                  <button 
                    @click="disconnect" 
                    :disabled="!isReady && !isConnecting"
                    class="btn btn-outline-danger me-2"
                  >
                    <i class="fas fa-unlink"></i>
                    Desconectar
                  </button>

                  <button 
                    @click="refreshStatus" 
                    :disabled="loading"
                    class="btn btn-outline-secondary me-2"
                  >
                    <i class="fas fa-sync-alt"></i>
                    Actualizar
                  </button>

                  <button 
                    @click="runDiagnostics" 
                    :disabled="loading"
                    class="btn btn-outline-info"
                  >
                    <i class="fas fa-stethoscope"></i>
                    Diagnóstico
                  </button>
                </div>
                <div class="col-md-4 text-end">
                  <div class="queue-info" v-if="queueLength > 0">
                    <small class="text-info">
                      <i class="fas fa-clock"></i>
                      {{ queueLength }} mensajes en cola
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- QR Code -->
            <div v-if="showQR" class="qr-section text-center mb-4">
              <div class="qr-container">
                <h5>Escanea este código QR con tu WhatsApp</h5>
                <div class="qr-code-wrapper">
                  <img :src="qrCode" alt="WhatsApp QR Code" class="qr-image" />
                </div>
                <div class="qr-instructions mt-3">
                  <p class="text-muted">
                    <i class="fab fa-whatsapp"></i>
                    Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo
                  </p>
                  <div class="qr-refresh mt-2">
                    <small class="text-muted">
                      El código QR se actualiza automáticamente cada 20 segundos
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Funcionalidades principales -->
    <div class="row" v-if="isReady">
      <!-- Envío individual -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>
              <i class="fas fa-comment"></i>
              Envío Individual
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="sendIndividualMessage">
              <div class="mb-3">
                <label class="form-label">
                  <i class="fas fa-phone"></i> Número de teléfono
                </label>
                <input 
                  v-model="individualMessage.phone" 
                  type="text" 
                  class="form-control"
                  placeholder="+56912345678"
                  required
                />
                <small class="form-text text-muted">
                  Formato: +56912345678 o 912345678
                </small>
              </div>
              <div class="mb-3">
                <label class="form-label">
                  <i class="fas fa-envelope"></i> Mensaje
                </label>
                <textarea 
                  v-model="individualMessage.text" 
                  class="form-control" 
                  rows="4"
                  placeholder="Escribe tu mensaje aquí..."
                  required
                ></textarea>
                <small class="form-text text-muted">
                  {{ individualMessage.text.length }}/1000 caracteres
                </small>
              </div>
              <div class="mb-3">
                <button 
                  @click="checkNumber" 
                  type="button" 
                  class="btn btn-sm btn-outline-info me-2"
                  :disabled="!individualMessage.phone || checking"
                >
                  <i v-if="checking" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-search"></i>
                  Verificar Número
                </button>
                <span v-if="numberCheckResult" class="text-sm">
                  <i :class="numberCheckResult.exists ? 'fas fa-check text-success' : 'fas fa-times text-danger'"></i>
                  {{ numberCheckResult.exists ? 'Número válido' : 'Número no encontrado' }}
                </span>
              </div>
              <button 
                type="submit" 
                :disabled="sending || !individualMessage.phone || !individualMessage.text"
                class="btn btn-primary"
              >
                <i v-if="sending" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-paper-plane"></i>
                Enviar Mensaje
              </button>
            </form>
          </div>
        </div>
      </div>

      <!-- Plantillas rápidas -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>
              <i class="fas fa-template"></i>
              Plantillas Rápidas
            </h5>
          </div>
          <div class="card-body">
            <!-- Plantillas predefinidas -->
            <div class="template-section mb-4">
              <h6>Plantillas del Gimnasio</h6>
              <div class="row">
                <div class="col-md-12 mb-2">
                  <button 
                    @click="openTemplateModal('membership_expiry')" 
                    class="btn btn-warning btn-sm w-100"
                  >
                    <i class="fas fa-bell"></i>
                    Vencimiento de Membresía
                  </button>
                </div>
                <div class="col-md-12 mb-2">
                  <button 
                    @click="openTemplateModal('payment_reminder')" 
                    class="btn btn-info btn-sm w-100"
                  >
                    <i class="fas fa-credit-card"></i>
                    Recordatorio de Pago
                  </button>
                </div>
                <div class="col-md-12 mb-2">
                  <button 
                    @click="openTemplateModal('welcome')" 
                    class="btn btn-success btn-sm w-100"
                  >
                    <i class="fas fa-hand-wave"></i>
                    Mensaje de Bienvenida
                  </button>
                </div>
                <div class="col-md-12 mb-2">
                  <button 
                    @click="openTemplateModal('promotion')" 
                    class="btn btn-purple btn-sm w-100"
                  >
                    <i class="fas fa-tag"></i>
                    Promoción Especial
                  </button>
                </div>
              </div>
            </div>

            <!-- Envío masivo -->
            <div class="bulk-section">
              <h6>Envío Masivo</h6>
              <div class="mb-3">
                <label class="form-label">Mensaje masivo</label>
                <textarea 
                  v-model="bulkMessage.text" 
                  class="form-control" 
                  rows="3"
                  placeholder="Mensaje para envío masivo. Usa {name} para personalizar con el nombre del cliente."
                ></textarea>
              </div>
              <div class="mb-3">
                <label class="form-label">Números (uno por línea)</label>
                <textarea 
                  v-model="bulkMessage.numbers" 
                  class="form-control" 
                  rows="4"
                  placeholder="+56912345678&#10;+56987654321&#10;+56911111111"
                ></textarea>
                <small class="form-text text-muted">
                  {{ bulkNumbersCount }} números detectados
                </small>
              </div>
              <button 
                @click="sendBulkMessage" 
                :disabled="!bulkMessage.text || !bulkMessage.numbers || sendingBulk"
                class="btn btn-primary"
              >
                <i v-if="sendingBulk" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-broadcast-tower"></i>
                Enviar Masivo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensajes recientes -->
    <div class="row" v-if="isReady">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h5>
              <i class="fas fa-comments"></i>
              Mensajes Recientes
              <small class="text-muted">(Últimos 10 mensajes recibidos)</small>
            </h5>
          </div>
          <div class="card-body">
            <div v-if="recentMessages.length === 0" class="text-center text-muted py-4">
              <i class="fas fa-inbox fa-2x mb-3"></i>
              <p>No hay mensajes recientes</p>
            </div>
            <div v-else>
              <div 
                v-for="message in recentMessages" 
                :key="message.id"
                class="message-item mb-3 p-3 border rounded"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <div class="message-header mb-2">
                      <strong>{{ message.fromName }}</strong>
                      <small class="text-muted ms-2">{{ message.from }}</small>
                    </div>
                    <div class="message-body">
                      {{ message.body }}
                    </div>
                  </div>
                  <div class="message-meta text-end">
                    <small class="text-muted">
                      {{ formatDate(message.timestamp) }}
                    </small>
                    <div class="message-actions mt-1">
                      <button 
                        @click="replyToMessage(message)" 
                        class="btn btn-sm btn-outline-primary"
                      >
                        <i class="fas fa-reply"></i>
                        Responder
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de plantillas -->
    <div class="modal fade" id="templateModal" tabindex="-1" ref="templateModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ currentTemplate.title }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="sendTemplateMessage">
              <div class="mb-3">
                <label class="form-label">Número de teléfono</label>
                <input 
                  v-model="templateForm.phone" 
                  type="text" 
                  class="form-control"
                  required
                />
              </div>
              <div class="row" v-for="field in currentTemplate.fields" :key="field.name">
                <div class="col-md-12 mb-3">
                  <label class="form-label">{{ field.label }}</label>
                  <input 
                    v-model="templateForm.data[field.name]" 
                    :type="field.type || 'text'"
                    class="form-control"
                    :placeholder="field.placeholder"
                    required
                  />
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Vista previa del mensaje</label>
                <div class="preview-message p-3 bg-light rounded">
                  {{ templatePreview }}
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  :disabled="sendingTemplate"
                  class="btn btn-primary"
                >
                  <i v-if="sendingTemplate" class="fas fa-spinner fa-spin"></i>
                  Enviar Mensaje
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de progreso de envío masivo -->
    <div class="modal fade" id="bulkProgressModal" tabindex="-1" ref="bulkProgressModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Enviando Mensajes Masivos</h5>
          </div>
          <div class="modal-body">
            <div class="progress mb-3">
              <div 
                class="progress-bar" 
                :style="{ width: bulkProgress.percentage + '%' }"
              ></div>
            </div>
            <div class="text-center">
              <div class="mb-2">
                <strong>{{ bulkProgress.current }} de {{ bulkProgress.total }}</strong>
              </div>
              <div class="text-muted">
                Enviando a: {{ bulkProgress.currentPhone }}
              </div>
              <div class="mt-3">
                <small class="text-success">
                  <i class="fas fa-check"></i> 
                  Exitosos: {{ bulkProgress.successful }}
                </small>
                <small class="text-danger ms-3">
                  <i class="fas fa-times"></i> 
                  Fallidos: {{ bulkProgress.failed }}
                </small>
              </div>
            </div>
          </div>
          <div class="modal-footer" v-if="bulkProgress.completed">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import whatsappService from '@/services/whatsappService';

export default {
  name: 'WhatsappWebConector',
  data() {
    return {
      // Estado de conexión
      isReady: false,
      isConnecting: false,
      loading: false,
      connectionStatus: 'disconnected',
      qrCode: null,
      lastError: null,
      queueLength: 0,
      clientInfo: null,
      loadingTimeout: null, // Para timeout de seguridad del modal

      // Mensajes individuales
      individualMessage: {
        phone: '',
        text: ''
      },
      sending: false,
      checking: false,
      numberCheckResult: null,

      // Mensajes masivos
      bulkMessage: {
        text: '',
        numbers: ''
      },
      sendingBulk: false,
      bulkProgress: {
        current: 0,
        total: 0,
        percentage: 0,
        currentPhone: '',
        successful: 0,
        failed: 0,
        completed: false
      },

      // Plantillas
      currentTemplate: {
        type: '',
        title: '',
        fields: []
      },
      templateForm: {
        phone: '',
        data: {}
      },
      sendingTemplate: false,

      // Mensajes recientes
      recentMessages: [],

      // Plantillas disponibles
      templates: {
        membership_expiry: {
          title: 'Vencimiento de Membresía',
          fields: [
            { name: 'customerName', label: 'Nombre del Cliente', type: 'text', placeholder: 'Juan Pérez' },
            { name: 'expiryDate', label: 'Fecha de Vencimiento', type: 'date', placeholder: '2024-12-31' }
          ]
        },
        payment_reminder: {
          title: 'Recordatorio de Pago',
          fields: [
            { name: 'customerName', label: 'Nombre del Cliente', type: 'text', placeholder: 'Juan Pérez' },
            { name: 'amount', label: 'Monto Pendiente', type: 'number', placeholder: '50000' },
            { name: 'dueDate', label: 'Fecha de Vencimiento', type: 'date', placeholder: '2024-12-31' }
          ]
        },
        welcome: {
          title: 'Mensaje de Bienvenida',
          fields: [
            { name: 'customerName', label: 'Nombre del Cliente', type: 'text', placeholder: 'Juan Pérez' }
          ]
        },
        promotion: {
          title: 'Promoción Especial',
          fields: [
            { name: 'customerName', label: 'Nombre del Cliente', type: 'text', placeholder: 'Juan Pérez' },
            { name: 'promotionText', label: 'Texto de la Promoción', type: 'text', placeholder: '50% de descuento en membresías anuales' }
          ]
        }
      }
    };
  },

  computed: {
    // Obtener ID de empresa del store
    idEmpresa() {
      return this.$store.getters.getUserData?.empresa?._id || 
             sessionStorage.getItem('eId') || 
             'default';
    },

    showQR() {
      return this.qrCode && !this.isReady;
    },

    statusClass() {
      const classes = {
        'connected': 'alert-success',
        'connecting': 'alert-info',
        'disconnected': 'alert-secondary',
        'error': 'alert-danger'
      };
      return classes[this.connectionStatus] || 'alert-secondary';
    },

    statusIcon() {
      const icons = {
        'connected': 'fas fa-check-circle',
        'connecting': 'fas fa-spinner fa-spin',
        'disconnected': 'fas fa-unlink',
        'error': 'fas fa-exclamation-triangle'
      };
      return icons[this.connectionStatus] || 'fas fa-question-circle';
    },

    statusTitle() {
      const titles = {
        'connected': 'Conectado',
        'connecting': 'Conectando...',
        'disconnected': 'Desconectado',
        'error': 'Error de Conexión'
      };
      return titles[this.connectionStatus] || 'Estado Desconocido';
    },

    statusMessage() {
      if (this.connectionStatus === 'connected') {
        return 'WhatsApp Web está conectado y listo para enviar mensajes';
      } else if (this.connectionStatus === 'connecting') {
        return 'Conectando con WhatsApp Web...';
      } else if (this.connectionStatus === 'error') {
        return this.lastError || 'Error desconocido';
      } else {
        return 'WhatsApp Web no está conectado';
      }
    },

    connectionButtonText() {
      if (this.isConnecting) return 'Conectando...';
      if (this.isReady) return 'Conectado';
      return 'Conectar WhatsApp';
    },

    bulkNumbersCount() {
      if (!this.bulkMessage.numbers) return 0;
      return this.bulkMessage.numbers.split('\n').filter(n => n.trim()).length;
    },

    templatePreview() {
      if (!this.currentTemplate.type) return '';
      
      // Simular el mensaje con los datos del formulario
      const templates = {
        membership_expiry: (data) => 
          `¡Hola ${data.customerName || '[Nombre]'}! 🏋️‍♂️\n\nTu membresía en CentralGym expira el ${data.expiryDate || '[Fecha]'}.\n¡Renuévala para seguir entrenando!\n\n💪 ¡Te esperamos en el gimnasio!`,
        payment_reminder: (data) => 
          `Hola ${data.customerName || '[Nombre]'} 💳\n\nTienes un pago pendiente de $${data.amount || '[Monto]'} vencido el ${data.dueDate || '[Fecha]'}.\n\n¡Regulariza tu situación para seguir entrenando sin interrupciones!`,
        welcome: (data) => 
          `¡Bienvenido a CentralGym, ${data.customerName || '[Nombre]'}! 🎉\n\nTu membresía está activa desde hoy.\n¡Disfruta de todas nuestras instalaciones y clases!\n\n🏋️‍♂️ ¡Que tengas excelentes entrenamientos!`,
        promotion: (data) =>
          `🎉 ¡PROMOCIÓN ESPECIAL! 🎉\n\nHola ${data.customerName || '[Nombre]'},\n\n${data.promotionText || '[Texto de promoción]'}\n\n¡No te pierdas esta oportunidad! 🏋️‍♂️`
      };

      return templates[this.currentTemplate.type] ? 
        templates[this.currentTemplate.type](this.templateForm.data) : '';
    }
  },

  methods: {
    // Inicializar WhatsApp
    async initializeWhatsApp() {
      console.log('🚀 Iniciando WhatsApp...');
      console.log('🏢 ID Empresa:', this.idEmpresa);
      console.log('📊 Estado actual:', {
        isReady: this.isReady,
        isConnecting: this.isConnecting,
        connectionStatus: this.connectionStatus
      });

      if (!this.idEmpresa) {
        this.$notify({
          title: 'Error',
          message: 'ID de empresa no encontrado. Por favor, recarga la página.',
          type: 'error'
        });
        return;
      }

      this.loading = true;
      this.isConnecting = true;
      this.connectionStatus = 'connecting';
      this.qrCode = null; // Limpiar QR anterior
      
      // Timeout de seguridad para cerrar modal después de 2 minutos
      this.loadingTimeout = setTimeout(() => {
        if (this.loading) {
          console.warn('⚠️ Timeout de seguridad: cerrando modal después de 2 minutos');
          this.loading = false;
          this.isConnecting = false;
          this.connectionStatus = 'error';
          this.lastError = 'Timeout: WhatsApp Web tardó demasiado en cargar';
          this.$notify({
            title: 'Timeout',
            message: 'WhatsApp Web tardó demasiado en cargar. Intenta de nuevo.',
            type: 'error'
          });
        }
      }, 120000); // 2 minutos
      
      try {
        console.log('📡 Llamando a whatsappService.initialize...');
        const result = await whatsappService.initialize(this.idEmpresa);
        
        console.log('📝 Resultado de initialize:', result);
        
        if (result.success) {
          console.log('✅ Initialize exitoso, manteniendo loading hasta QR...');
          this.$notify({
            title: 'Conectando',
            message: result.message,
            type: 'success'
          });
          
          // NO poner loading = false aquí - esperar a eventos Socket.IO
          // El loading se pondrá en false cuando llegue QR, ready, o error
          
        } else {
          console.error('❌ Error en initialize:', result.message);
          this.$notify({
            title: 'Error',
            message: result.message,
            type: 'error'
          });
          this.connectionStatus = 'error';
          this.isConnecting = false;
          this.loading = false; // Solo aquí ponemos loading false si hay error
        }
      } catch (error) {
        console.error('❌ Exception en initializeWhatsApp:', error);
        this.$notify({
          title: 'Error',
          message: 'Error al inicializar WhatsApp: ' + error.message,
          type: 'error'
        });
        this.connectionStatus = 'error';
        this.isConnecting = false;
        this.loading = false; // Solo aquí ponemos loading false si hay error
      }
      // NO poner finally con loading = false - se manejará por eventos Socket.IO
      
      console.log('🏁 initializeWhatsApp completado, esperando eventos...');
      this.updateStatus();
    },

    // Desconectar WhatsApp
    async disconnect() {
      try {
        const result = await whatsappService.disconnect(this.idEmpresa);
        this.$notify({
          title: result.success ? 'Éxito' : 'Error',
          message: result.message,
          type: result.success ? 'success' : 'error'
        });
        this.updateStatus();
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Error al desconectar: ' + error.message,
          type: 'error'
        });
      }
    },

    // Actualizar estado
    async updateStatus() {
      try {
        const status = await whatsappService.getStatus(this.idEmpresa);
        this.isReady = status.isReady;
        this.isConnecting = status.isConnecting;
        this.connectionStatus = status.connectionStatus;
        this.qrCode = status.qrCode;
        this.lastError = status.lastError;
        this.queueLength = status.queueLength || 0;
        this.clientInfo = status.clientInfo;
      } catch (error) {
        console.error('Error obteniendo estado WhatsApp:', error);
        this.connectionStatus = 'error';
        this.lastError = error.message;
      }
    },

    // Refrescar estado
    refreshStatus() {
      this.updateStatus();
      this.updateClientInfo();
    },

    // Actualizar información del cliente
    async updateClientInfo() {
      if (this.isReady) {
        this.clientInfo = await whatsappService.getClientInfo();
      } else {
        this.clientInfo = null;
      }
    },

    // Verificar número
    async checkNumber() {
      if (!this.individualMessage.phone) return;
      
      this.checking = true;
      try {
        this.numberCheckResult = await whatsappService.checkNumberExists(this.idEmpresa, this.individualMessage.phone);
      } catch (error) {
        this.numberCheckResult = { exists: false, error: error.message };
      } finally {
        this.checking = false;
      }
    },

    // Enviar mensaje individual
    async sendIndividualMessage() {
      this.sending = true;
      try {
        const result = await whatsappService.sendMessage(
          this.idEmpresa,
          this.individualMessage.phone,
          this.individualMessage.text
        );

        this.$notify({
          title: result.success ? 'Éxito' : 'Error',
          message: result.message,
          type: result.success ? 'success' : 'error'
        });

        if (result.success) {
          this.individualMessage = { phone: '', text: '' };
          this.numberCheckResult = null;
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Error al enviar mensaje: ' + error.message,
          type: 'error'
        });
      } finally {
        this.sending = false;
      }
    },

    // Enviar mensaje masivo
    async sendBulkMessage() {
      if (!this.bulkMessage.text || !this.bulkMessage.numbers) return;

      const numbers = this.bulkMessage.numbers.split('\n').filter(n => n.trim());
      if (numbers.length === 0) return;

      this.sendingBulk = true;
      this.bulkProgress = {
        current: 0,
        total: numbers.length,
        percentage: 0,
        currentPhone: '',
        successful: 0,
        failed: 0,
        completed: false
      };

      // Mostrar modal de progreso
      const modal = new bootstrap.Modal(this.$refs.bulkProgressModal);
      modal.show();

      try {
        const response = await whatsappService.sendBulkMessages(this.idEmpresa, numbers, this.bulkMessage.text);
        
        this.bulkProgress.completed = true;
        // Note: En modo async, los resultados vienen por Socket.IO eventos
        // this.bulkProgress.successful = results.filter(r => r.success).length;
        // this.bulkProgress.failed = results.filter(r => !r.success).length;

        this.$notify({
          title: 'Envío Masivo Completado',
          message: `Enviados: ${this.bulkProgress.successful}, Fallidos: ${this.bulkProgress.failed}`,
          type: 'success'
        });

        // Limpiar formulario
        this.bulkMessage = { text: '', numbers: '' };
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Error en envío masivo: ' + error.message,
          type: 'error'
        });
      } finally {
        this.sendingBulk = false;
      }
    },

    // Abrir modal de plantilla
    openTemplateModal(templateType) {
      this.currentTemplate = {
        type: templateType,
        ...this.templates[templateType]
      };
      this.templateForm = {
        phone: '',
        data: {}
      };

      const modal = new bootstrap.Modal(this.$refs.templateModal);
      modal.show();
    },

    // Enviar mensaje con plantilla
    async sendTemplateMessage() {
      this.sendingTemplate = true;
      try {
        const result = await whatsappService.sendTemplateMessage(
          this.idEmpresa,
          this.templateForm.phone,
          this.currentTemplate.type,
          this.templateForm.data
        );

        this.$notify({
          title: result.success ? 'Éxito' : 'Error',
          message: result.message,
          type: result.success ? 'success' : 'error'
        });

        if (result.success) {
          // Cerrar modal
          const modal = bootstrap.Modal.getInstance(this.$refs.templateModal);
          modal.hide();
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Error al enviar plantilla: ' + error.message,
          type: 'error'
        });
      } finally {
        this.sendingTemplate = false;
      }
    },

    // Responder a mensaje
    replyToMessage(message) {
      this.individualMessage.phone = message.from.replace('@c.us', '');
      this.individualMessage.text = '';
      
      // Scroll al formulario de envío
      this.$nextTick(() => {
        const element = document.querySelector('.card-body form');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    // Formatear fecha
    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleString('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Configurar eventos del servicio
    setupServiceEvents() {
      console.log('🔧 Configurando eventos del servicio WhatsApp...');
      
      // Evento QR con más debug
      whatsappService.on('qr', (data) => {
        console.log('🔍 QR Event recibido en componente:', data);
        console.log('🔍 QR Code existe:', !!data.qrCode);
        console.log('🔍 QR Code length:', data.qrCode ? data.qrCode.length : 0);
        console.log('🔍 QR Code type:', typeof data.qrCode);
        
        if (data.qrCode) {
          console.log('🔍 QR Code preview:', data.qrCode.substring(0, 100));
          this.qrCode = data.qrCode;
          this.connectionStatus = 'qr_ready';
          this.loading = false; // ✅ Cerrar modal cuando llegue QR
          this.isConnecting = false; // Ya no está "conectando", tiene QR
          
          // Limpiar timeout de seguridad
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }
          
          // Forzar actualización reactiva
          this.$forceUpdate();
          
          // Notificar al usuario
          this.$notify({
            title: 'Código QR Disponible',
            message: 'Escanea el código QR con tu WhatsApp para conectar',
            type: 'warning',
            duration: 5000
          });
          
          console.log('✅ QR Code configurado en componente exitosamente');
        } else {
          console.warn('⚠️ QR Event recibido pero sin datos de QR');
        }
      });

      // Evento listo
      whatsappService.on('ready', () => {
        console.log('✅ WhatsApp listo - actualizando estado');
        this.qrCode = null; // Limpiar QR cuando esté listo
        this.loading = false; // ✅ Cerrar modal cuando esté listo
        this.isConnecting = false;
        
        // Limpiar timeout de seguridad
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        
        this.updateStatus();
        this.updateClientInfo();
        
        this.$notify({
          title: 'WhatsApp Conectado',
          message: '¡WhatsApp está listo para enviar mensajes!',
          type: 'success'
        });
      });

      // Evento desconectado
      whatsappService.on('disconnected', () => {
        console.log('⚠️ WhatsApp desconectado - actualizando estado');
        this.loading = false; // ✅ Cerrar modal si se desconecta
        this.isConnecting = false;
        
        // Limpiar timeout de seguridad
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        
        this.updateStatus();
        this.clientInfo = null;
        this.qrCode = null;
      });

      // Evento mensaje recibido
      whatsappService.on('messageReceived', (message) => {
        console.log('📨 Mensaje recibido en componente:', message.from);
        this.recentMessages.unshift(message);
        if (this.recentMessages.length > 10) {
          this.recentMessages.pop();
        }
        
        // Notificación de mensaje recibido
        this.$notify({
          title: 'Mensaje Recibido',
          message: `De: ${message.fromName} - ${message.body.substring(0, 50)}...`,
          type: 'success'
        });
      });

      // Evento progreso de envío masivo
      whatsappService.on('bulkProgress', (progress) => {
        console.log('📊 Progreso envío masivo:', progress);
        this.bulkProgress.current = progress.current;
        this.bulkProgress.total = progress.total;
        this.bulkProgress.percentage = Math.round((progress.current / progress.total) * 100);
        this.bulkProgress.currentPhone = progress.phone;
        
        if (progress.success) {
          this.bulkProgress.successful++;
        } else {
          this.bulkProgress.failed++;
        }
      });

      // Evento cambio de estado
      whatsappService.on('statusChange', (data) => {
        console.log('🔄 Cambio de estado WhatsApp:', data);
        this.connectionStatus = data.status;
        
        // Si el estado incluye QR code, configurarlo
        if (data.qrCode && data.status === 'qr_ready') {
          console.log('🔍 QR Code recibido en status change:', !!data.qrCode);
          this.qrCode = data.qrCode;
          this.loading = false; // ✅ Cerrar modal cuando llegue QR en status change
          this.isConnecting = false;
          this.$forceUpdate();
        }
      });

      // Evento error
      whatsappService.on('error', (data) => {
        console.error('❌ Error WhatsApp en componente:', data);
        this.lastError = data.error;
        this.connectionStatus = 'error';
        this.isConnecting = false;
        this.loading = false; // ✅ Cerrar modal cuando haya error
        
        // Limpiar timeout de seguridad
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        
        this.$notify({
          title: 'Error WhatsApp',
          message: data.error,
          type: 'error'
        });
      });

      // Evento de carga/loading
      whatsappService.on('loading', (data) => {
        console.log('⏳ Loading event:', data);
        // NO sobrescribir loading aquí - se maneja por otros eventos
        // this.loading = data.loading || false;
      });
      
      console.log('✅ Eventos configurados exitosamente');
    },

    // Método para ejecutar el diagnóstico QR específico
    async runQRDiagnostic() {
      console.log('🔍 Ejecutando diagnóstico QR específico...');
      
      try {
        // 1. Verificar conexión Socket.IO
        console.log('🔍 Estado Socket.IO:', whatsappService.socket.connected);
        
        // 2. Verificar estado del servicio
        const status = await whatsappService.getStatus(this.idEmpresa);
        console.log('🔍 Estado actual del servicio:', status);
        
        // 3. Verificar eventos Socket.IO
        console.log('🔍 Eventos registrados:', Object.keys(whatsappService.eventCallbacks));
        
        // 4. Intentar forzar actualización de estado
        await this.updateStatus();
        
        // 5. Mostrar información de debugging
        this.$notify({
          title: 'Diagnóstico QR',
          message: `Socket: ${whatsappService.socket.connected ? 'Conectado' : 'Desconectado'}, Estado: ${this.connectionStatus}, QR: ${!!this.qrCode}`,
          type: 'warning',
          duration: 10000
        });
        
        return {
          success: true,
          socketConnected: whatsappService.socket.connected,
          connectionStatus: this.connectionStatus,
          hasQR: !!this.qrCode,
          idEmpresa: this.idEmpresa
        };
      } catch (error) {
        console.error('❌ Error en diagnóstico QR:', error);
        this.$notify({
          title: 'Error en Diagnóstico',
          message: error.message,
          type: 'error'
        });
        return { success: false, error: error.message };
      }
    },

    // Método para ejecutar el diagnóstico
    async runDiagnostics() {
      console.log('🔍 Ejecutando diagnóstico completo...');
      
      try {
        // Ejecutar diagnóstico QR específico
        const qrDiagnostic = await this.runQRDiagnostic();
        
        // Ejecutar diagnóstico del backend también
        const result = await whatsappService.runDiagnostics(this.idEmpresa);
        
        const diagnosticMessage = `
QR Diagnóstico: ${qrDiagnostic.success ? 'OK' : 'Error'}
Socket.IO: ${qrDiagnostic.socketConnected ? 'Conectado' : 'Desconectado'}
Estado: ${qrDiagnostic.connectionStatus}
QR Code: ${qrDiagnostic.hasQR ? 'Disponible' : 'No disponible'}
Backend: ${result.success ? result.message : result.message}
        `;
        
        this.$notify({
          title: result.success ? 'Diagnóstico Completado' : 'Diagnóstico con Errores',
          message: diagnosticMessage,
          type: result.success ? 'success' : 'warning',
          duration: 15000
        });
        
        this.updateStatus();
        
      } catch (error) {
        console.error('❌ Error en diagnóstico:', error);
        this.$notify({
          title: 'Error',
          message: 'Error al ejecutar el diagnóstico: ' + error.message,
          type: 'error'
        });
      }
    }
  },

  async mounted() {
    // Configurar eventos del servicio
    this.setupServiceEvents();
    
    // Actualizar estado inicial
    await this.updateStatus();
    await this.updateClientInfo();

    // Actualizar estado cada 5 segundos
    this.statusInterval = setInterval(async () => {
      await this.updateStatus();
    }, 5000);
  },

  beforeDestroy() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  }
};
</script>

<style scoped>
.whatsapp-web-connector {
  padding: 20px 0;
}

.qr-container {
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  margin: 0 auto;
  max-width: 400px;
}

.qr-code-wrapper {
  display: inline-block;
  padding: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.qr-image {
  width: 200px;
  height: 200px;
  border-radius: 5px;
}

.qr-instructions {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
}

.connection-status .alert {
  border: none;
  border-radius: 10px;
  padding: 20px;
}

.connection-info {
  font-size: 0.9em;
}

.template-section {
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.bulk-section {
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.btn-purple {
  background-color: #6f42c1;
  border-color: #6f42c1;
  color: white;
}

.btn-purple:hover {
  background-color: #5a359a;
  border-color: #5a359a;
  color: white;
}

.message-item {
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.message-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.message-header {
  font-size: 0.9em;
}

.message-body {
  font-size: 1em;
  line-height: 1.4;
  color: #333;
}

.message-meta {
  min-width: 120px;
}

.message-actions {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.message-item:hover .message-actions {
  opacity: 1;
}

.progress {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
}

.progress-bar {
  background-color: #28a745;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.preview-message {
  white-space: pre-line;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.95em;
  line-height: 1.4;
}

.queue-info {
  background: #e3f2fd;
  padding: 5px 10px;
  border-radius: 15px;
  display: inline-block;
}

.card-header h4,
.card-header h5 {
  margin: 0;
  font-weight: 600;
}

.card-header .fab.fa-whatsapp {
  font-size: 1.2em;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
}

.text-sm {
  font-size: 0.875em;
}

@media (max-width: 768px) {
  .qr-container {
    padding: 20px;
  }
  
  .qr-image {
    width: 150px;
    height: 150px;
  }
  
  .message-item {
    padding: 15px;
  }
  
  .message-meta {
    min-width: auto;
    margin-top: 10px;
  }
  
  .connection-controls .btn {
    margin-bottom: 10px;
  }
}
</style> 